'use client'

import {
  ApolloClient,
  ApolloProvider as ApolloClientProvider,
  defaultDataIdFromObject,
  InMemoryCache,
  HttpLink,
  NormalizedCacheObject,
  split,
} from '@apollo/client'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { RetryLink } from '@apollo/client/link/retry'
import { setContext } from '@apollo/client/link/context'
import fetch from 'unfetch'
import React, { useEffect, useState } from 'react'
import localForage from 'localforage'
import { CachePersistor, LocalForageWrapper } from 'apollo3-cache-persist'
import { createContainer } from 'unstated-next'
import { TokenStorage } from 'lib/TokenStorage'
import config from '../../config'
import { educationQuestPossibleTypes, educationQuestTypePolicies } from './educationQuestTypes'
import { curriculumPossibleTypes } from './curriculumTypes'


const batchLink = new BatchHttpLink({
  // @ts-expect-error
  fetch,
  uri: config.graphqlEndpoint,
  batchMax: 5,
  batchInterval: 20,
})

// Critical home requests can finish independently of optional promotional data.
const immediateLink = new HttpLink({
  // @ts-expect-error
  fetch,
  uri: config.graphqlEndpoint,
})

const retryLink = new RetryLink({
  delay: {
    initial: 1000,
    max: 10000,
  },
  attempts: {
    max: 3,
    retryIf: (error, _operation) => {
      return !!error
    },
  },
})

const authLink = setContext(async (_, { headers }) => {
  const rawToken = await TokenStorage.getItem('token')
  let token = rawToken?.value
  if (!token) {
    token = sessionStorage.getItem('token')
  }

  if (token) {
    return {
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
      },
    }
  }
})

export const clearCache = async () => {
  const client = ApolloClientInstance.getInstance()
  client.stop()
  await client.clearStore()
}

const cache = new InMemoryCache({
  possibleTypes: {
    ...educationQuestPossibleTypes,
    ...curriculumPossibleTypes,
    NZKSubscription: [
      'NZKStripeSubscription',
      'NZKAppleSubscription',
      'NZKCustomSubscription',
    ],
  },
  typePolicies: {
    ...educationQuestTypePolicies,
    User: {
      fields: {
        nzkSubscription: {
          merge: true,
        },
      },
    },
    NZKStripeSubscription: {
      fields: {
        stripeCustomer: {
          merge: true,
        },
      },
    },
    Card: {
      keyFields: false
    },
    StarTutoringSubscription: {
      fields: {
        schedule: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming)
          },
        },
      },
    },
  },
  dataIdFromObject: (object) => {
    switch (object.__typename) {
      case 'UserCurriculumGroup':
        return false // Prevents caching stats in common group
      default:
        return defaultDataIdFromObject(object)
    }
  },
})

// eslint-disable-next-line
export let persistor: CachePersistor<any>

export class ApolloClientInstance {
  static client: ApolloClient<NormalizedCacheObject> | null = null

  static getInstance() {
    if (this.client) return this.client

    this.client = new ApolloClient({
      cache,
      connectToDevTools: true,
      link: authLink.concat(retryLink).concat(split(operation => operation.getContext().unbatched === true, immediateLink, batchLink)),
    })

    return this.client
  }
}

export const apolloClient = ApolloClientInstance.getInstance()

let cacheRestorePromise: Promise<void> | null = null

export const restoreApolloCache = () => {
  if (typeof window === 'undefined') return Promise.resolve()
  if (cacheRestorePromise) return cacheRestorePromise

  persistor = new CachePersistor({
    cache,
    storage: new LocalForageWrapper(localForage),
    debug: true,
    trigger: 'write',
  })
  // Keep cache restoration failures visible without preventing the app from loading.
  // eslint-disable-next-line no-console
  cacheRestorePromise = persistor.restore().catch(console.error)
  return cacheRestorePromise
}

if (typeof window !== 'undefined') restoreApolloCache()

const ApolloState = () => {
  const [cacheReady, setCacheReady] = useState(typeof window === 'undefined')

  useEffect(() => {
    let mounted = true
    restoreApolloCache().then(() => {
      if (mounted) setCacheReady(true)
    })
    return () => {
      mounted = false
    }
  }, [])

  return {
    clearCache,
    persistor,
    client: apolloClient,
    cacheReady,
  }
}

const ApolloStateContainer = createContainer(ApolloState)
const ApolloStateProvider = ApolloStateContainer.Provider
export const useApolloState = ApolloStateContainer.useContainer

const ApolloProvider = ({ children }: { children: any }) => {
  const { client } = useApolloState()

  useEffect(() => {
    // @ts-ignore
    window.clearCache = clearCache
  }, [])

  return <ApolloClientProvider client={client}>{children}</ApolloClientProvider>
}

export const Provider = ({ children }: { children: any }) => {
  return (
    <ApolloStateProvider>
      <ApolloProvider>{children}</ApolloProvider>
    </ApolloStateProvider>
  )
}
